import { AppSection, NomObjectClass } from '../../constants';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Dashboard, DataObject, Feed, User } from '../../models/core.models';
import { Observable, take, tap } from 'rxjs';
import {
  selectCoreAppSection,
  selectCurrentObject,
  selectCurrentObjectEditMode,
  selectPeriod,
  selectUser,
  selectUserAccessLevelGlobalAdmin,
  selectUserAccessLevelName,
  selectUserAlertsCount,
  selectUserCompany,
  selectUserNotificationsCount,
} from '../../reducers';

import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { CompanySwitcherComponent } from '../../../shared/components/company-switcher/company-switcher.component';
import { DropdownAlertsComponent } from '../dropdowns/dropdown-alerts/dropdown-alerts.component';
import { DropdownNotificationsComponent } from '../dropdowns/dropdown-notifications/dropdown-notifications.component';
import { DropdownUserComponent } from '../dropdowns/dropdown-user/dropdown-user.component';
import { ObjectCreatorService } from '../../services/object-creator.service';
import { PresentationService } from '../../services/presentation.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { UserAccessLevel } from '../../services/user.service';
import { UserFacadeService } from '@services/user-facade.service';
import { environment } from '@env/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toggleDarkMode } from '../../actions/core.actions';

import {
  HeaderTools,
  HeaderToolsService,
} from '../../services/header-tools.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent {
  public appName: string = environment.appName;
  NomObjectClass = NomObjectClass;
  public isMenuCollapsed: boolean = true;
  activeCompany: any;
  accessLevel: any;
  currentObject$: Observable<DataObject>;
  currentUser$: Observable<any>;

  countAlerts$: Observable<number>;
  countNotifications$: Observable<number>;
  currentObjectEditMode$: Observable<boolean>;
  isGlobalAdmin$: Observable<boolean>;

  /** Screen-registered header controls (date range / data-view toggle). */
  readonly headerTools$: Observable<HeaderTools | null> =
    inject(HeaderToolsService).currentTools$;

  private destroyRef = inject(DestroyRef);

  @ViewChild('alertsDropdownContainer', { read: ViewContainerRef })
  alertsContainer: ViewContainerRef;

  @ViewChild('notificationsDropdownContainer', { read: ViewContainerRef })
  notificationsContainer: ViewContainerRef;

  @ViewChild('userDropdownContainer', { read: ViewContainerRef })
  userContainer: ViewContainerRef;

  appSection: AppSection = 'analytics';
  activeMenu: 'alerts' | 'notifications' | 'user' | 'none' = 'none';

  AppComponents = AppFeatures;

  constructor(
    private store: Store<AppState>,
    private objectCreatorService: ObjectCreatorService,
    public userFacade: UserFacadeService,
    protected router: Router,
    private presentationService: PresentationService,
  ) {
    this.currentObject$ = this.store.select(selectCurrentObject);
    this.currentUser$ = this.store.select(selectUser);
    this.countAlerts$ = this.store.select(selectUserAlertsCount);
    this.countNotifications$ = this.store.select(selectUserNotificationsCount);
    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );
    this.isGlobalAdmin$ = this.store.select(selectUserAccessLevelGlobalAdmin);

    this.store
      .select(selectCoreAppSection)
      .pipe(
        tap((section) => {
          this.appSection = section;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserCompany)
      .pipe(
        tap((company) => {
          this.activeCompany = company?.name;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserAccessLevelName)
      .pipe(
        tap((accessLevelName) => {
          this.accessLevel = accessLevelName;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngOnInit(): void {}

  showAlerts(): void {
    if (this.activeMenu === 'alerts') {
      this.alertsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'alerts';

    this.alertsContainer.clear();
    this.notificationsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.alertsContainer.createComponent(
      DropdownAlertsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.alertsContainer.clear();
      if (this.activeMenu === 'alerts') {
        this.activeMenu = 'none';
      }
    };
  }

  showNotifications(): void {
    if (this.activeMenu === 'notifications') {
      this.notificationsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'notifications';

    this.notificationsContainer.clear();
    this.alertsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.notificationsContainer.createComponent(
      DropdownNotificationsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.notificationsContainer.clear();
      if (this.activeMenu === 'notifications') {
        this.activeMenu = 'none';
      }
    };
  }

  showUserSettings(): void {
    if (this.activeMenu === 'user') {
      this.userContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'user';

    this.userContainer.clear();
    this.notificationsContainer.clear();
    this.alertsContainer.clear();

    const componentRef = this.userContainer.createComponent(
      DropdownUserComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.userContainer.clear();
      if (this.activeMenu === 'user') {
        this.activeMenu = 'none';
      }
    };
  }

  createNewFeed() {
    this.objectCreatorService.createNewFeed();
  }

  createNewWorkspace() {
    this.objectCreatorService.createNewWorkspace();
  }

  createNewDashboard() {
    this.objectCreatorService.createNewDashboard();
  }

  createNewDimension() {
    this.objectCreatorService.createNewDimension();
  }

  createNewUseCase() {
    this.objectCreatorService.createNewUseCase();
  }

  toggleDarkMode() {
    this.store.dispatch(toggleDarkMode());
  }

  showPresentationMode(object: Feed | Dashboard) {
    if (object.object_class_id === NomObjectClass.DASHBOARD) {
      this.presentationService.showDashboardPresentation(object.id);
    } else if (object.object_class_id === NomObjectClass.FEED) {
      const objectClass = 'feed';

      this.store
        .select(selectPeriod)
        .pipe(
          take(1),
          tap((period) => {
            const url = this.router.serializeUrl(
              this.router.createUrlTree(
                [
                  environment.baseUrlEnvironmentSuffix,
                  'presentation',
                  objectClass,
                  object.id,
                ],
                {
                  queryParams: {
                    start_time: period.startTimestamp,
                    end_time: period.endTimestamp,
                  },
                },
              ),
            );

            window.open(url, '_blank');
          }),
        )
        .subscribe();
    }
  }
}
